<template>
  <div v-loading="loading" class="ec-line028">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item>
        <a :href="baseUrl">{{ this.shopName }}</a></el-breadcrumb-item
      >
      <el-breadcrumb-item :to="{ path: $routePage.EC_LINE009 }"> {{ $t('ec-line009.heading') }}</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: $routePage.EC_LINE014 }">{{ $t('ec-line014.title') }}</el-breadcrumb-item>
      <el-breadcrumb-item>{{ $t('ec-line028.title') }}</el-breadcrumb-item>
    </el-breadcrumb>
    <the-heading class="lg:mb-8 mb-5">{{ $t('ec-line028.heading') }}</the-heading>
    <template v-if="subscriptionConfirm">
      <div class="subscription-list-item">
        <the-section :title="$t('ec-line028.content.purchase.title')">
          <cart-list class="hidden lg:block">
            <cart-list-item :data="subscriptionConfirm.subscription_order_item"> </cart-list-item>
            <cart-total
              :sub-total="subTotal"
              :sub-discount="subDiscount"
              :delivery-fee="deliveryFee"
              :tax="tax"
              :total="totalAmount"
            />
          </cart-list>
          <cart-list class="block lg:hidden">
            <cart-list-item-sp
              v-for="(item, idx) in subscriptionConfirm.subscription_order_item"
              :key="idx"
              :data="item"
            ></cart-list-item-sp>
            <cart-total
              :sub-total="subTotal"
              :sub-discount="subDiscount"
              :delivery-fee="deliveryFee"
              :tax="tax"
              :total="totalAmount"
            />
          </cart-list>
        </the-section>
      </div>
      <the-section :title="$t('ec-line028.content.subscription.title')">
        <subscription-information :data="subscriptionConfirm.subscription_pattern"></subscription-information>
      </the-section>
      <the-section :title="$t('ec-line028.content.shipping.title')">
        <el-form label-position="top">
          <el-form-item :label="$t('common.label.shippingAddressCard')">
            <address-info-item
              v-if="subscriptionConfirm.shipping"
              class="address-info-item--bg-disabled"
              :full-name="fullName"
              :postal-code="subscriptionConfirm.shipping.postal_code"
              :address="
                Utils.helper.address(
                  prefectureText,
                  subscriptionConfirm.shipping.addr01,
                  subscriptionConfirm.shipping.addr02
                )
              "
              :building-name="subscriptionConfirm.shipping.addr03"
            />
          </el-form-item>
          <el-form-item :label="$t('common.label.shippingMethod')">
            <disabled-input :value="subscriptionConfirm.delivery_method_text" />
          </el-form-item>
          <template>
            <el-form-item
              v-if="subscriptionConfirm.delivery_method_text !== Constants.MAIL_DELIVERY_SERVICE"
              :label="$t('common.label.deliveryTimezone')"
            >
              <disabled-input :value="subscriptionConfirm.delivery_text" />
            </el-form-item>
            <el-form-item
              v-if="subscriptionConfirm.delivery_method_text !== Constants.MAIL_DELIVERY_SERVICE && !hasCorporateFlag"
              :label="$t('common.label.specifyDeliveryNote')"
            >
              <disabled-input :value="subscriptionConfirm.deliveryNotesText" />
            </el-form-item>
            <el-form-item v-if="!hasCorporateFlag" :label="$t('common.label.bundledFlag')"
              ><disabled-input :value="subscriptionConfirm.bundled_flag_text" />
            </el-form-item>
          </template>
        </el-form>
      </the-section>
      <the-section :title="$t('ec-line028.content.payment.title')">
        <payment-information :data="subscriptionConfirm"></payment-information>
      </the-section>
    </template>
    <el-row type="flex" justify="center" class="mx-4 my-5 lg:my-10 lg:mx-10">
      <i18n path="ec-line028.content.agreedText">
        <router-link :to="$routePage.EC_LINE030" class="underline font-bold" target="_blank"
          >{{ $t('common.termOfUse') }}
        </router-link>
        <router-link :to="$routePage.EC_LINE025" class="underline font-bold" target="_blank">{{
          $t('common.menu.privacyPolicy')
        }}</router-link>
      </i18n>
    </el-row>

    <div class="flex flex-wrap justify-center lg:mb-4">
      <el-button class="submit-btn lg:ml-6 lg:order-1" @click="submit">{{
        subscriptionConfirm.reopen_flag ? $t('ec-line028.content.reopenButton') : $t('ec-line028.content.submitBtn')
      }}</el-button>
      <el-button class="cancel-btn mt-5 mb-1 lg:my-0" @click="backToEditPage">
        {{ $t('common.action.cancel') }}
      </el-button>
    </div>

    <prompt-dialog
      :visible.sync="upablePaymentUpdateFlag"
      :title="$t('ec-line028.unablePaymentUpdateDialog.title')"
      @on-cancel="upablePaymentUpdateFlag = false"
    >
      <template #content>
        <div class="my-10">
          <pre class="whitespace-pre-line text-left text-red font-bold">
            {{ $t('ec-line028.unablePaymentUpdateDialog.content') }}
          </pre>
        </div>
      </template>
    </prompt-dialog>
  </div>
</template>

<script>
import { cloneDeep, isNil } from 'lodash'
import amazonMixin from '@/mixins/amazonV2'
import subscriptionMixin from '@/mixins/subscription'
import TheSection from '@/components/my-page-partials/TheSection.vue'
import TheHeading from '@/components/my-page-partials/TheHeading.vue'
import CartList from '@/components/ec-line028/CartList.vue'
import CartListItem from '@/components/ec-line028/CartListItem.vue'
import CartListItemSp from '@/components/ec-line028/CartListItemSp.vue'
import SubscriptionInformation from '@/components/ec-line028/SubscriptionInformation.vue'
import PaymentInformation from '@/components/ec-line028/PaymentInformation.vue'
import AddressInfoItem from '@/components/AddressInfoItem.vue'
import DisabledInput from '@/components/DisabledInput.vue'
import CartTotal from '@/components/CartTotal.vue'
import PromptDialog from '@/components/PromptDialog.vue'
import { AMAZON } from '@/constants/amazon'
import BreadcrumbMixin from '@/mixins/breadcrumb'
import gmoMixin from '@/mixins/gmo'
import { MyPageService, SubscriptionService } from '@/services'
import { APP_STATUS } from '@/constants'

export default {
  components: {
    TheHeading,
    TheSection,
    CartList,
    CartListItem,
    SubscriptionInformation,
    PaymentInformation,
    CartTotal,
    AddressInfoItem,
    DisabledInput,
    PromptDialog,
    CartListItemSp,
  },
  mixins: [amazonMixin, BreadcrumbMixin, subscriptionMixin, gmoMixin],
  props: {
    id: { type: [Number, String], default: () => null },
  },
  data() {
    return {
      loading: false,
      upablePaymentUpdateFlag: false,
    }
  },
  computed: {
    subscription() {
      return this.$store.getters['subscription/getSubscription']
    },
    subscriptionConfirm() {
      return this.$store.getters['subscription/getConfirmSubscription']
    },
    subTotal() {
      return this.subscription.subtotal
    },
    subDiscount() {
      return this.subscription.discount_amount
    },
    deliveryFee() {
      return this.subscription.delivery_fee
    },
    tax() {
      return this.subscription.tax_total
    },
    totalAmount() {
      return this.subscription.payment_total
    },
    isCreditCard() {
      return (
        this.subscriptionConfirm.payment_methods.find((i) => i.id === this.subscriptionConfirm.payment.payment_id)
          ?.payment_service_id === this.Constants.PAYMENT_METHOD.CREDIT_CARD
      )
    },
    isAmazon() {
      return (
        this.subscriptionConfirm.payment_methods.find((i) => i.id === this.subscriptionConfirm.payment.payment_id)
          ?.payment_service_id === this.Constants.PAYMENT_METHOD.AMAZON
      )
    },
    isCurrentAmazon() {
      return this.subscription.payment_service_id === this.Constants.PAYMENT_METHOD.AMAZON
    },
    prefectureText() {
      return (
        this.subscriptionConfirm.shipping?.pref?.name ||
        this.prefectures?.find((pref) => pref.id === this.subscriptionConfirm.shipping?.pref.id)?.name ||
        ''
      )
    },
    isReopenOnTheDay() {
      if (this.subscription.reopen_date !== null) {
        const reopenDate = this.$date.startOfDay(this.subscription.reopen_date)
        const today = this.$date.startOfToday()
        return this.$date.isEqual(reopenDate, today)
      }
      return false
    },
    hasCorporateFlag() {
      return this.$store.getters['auth/shop']?.corporate_flag
    },
    fullName() {
      const shippingInfo = this.subscriptionConfirm.shipping
      const storeName = this.hasCorporateFlag ? shippingInfo.store_name : ''
      return storeName + ' ' + this.Utils.helper.fullName(shippingInfo.name01, shippingInfo.name02)
    },
    default_bundled_flag() {
      return this.$store.getters['auth/shop'].default_bundled_flag
    },
    visible_bundled_flag() {
      return this.$store.getters['auth/shop'].visible_bundled_flag
    },
  },

  created() {
    if (this.isLoginAmazon) {
      this.processAmazonRedirect()
    }

    if (!this.subscriptionConfirm || !this.id || this.id !== this.subscriptionConfirm.id) {
      this.$router.push({ name: 'EC_LINE014' })
    }
  },
  methods: {
    backToEditPage() {
      this.$router.replace({ name: 'EC_LINE015', param: { id: this.id } })
    },
    async submit() {
      if (this.isAmazon && this.isLoginAmazon) {
        this.authorizeByAmazon()
      } else {
        this.onUpdateSubscription()
      }
    },
    async onUpdateSubscription() {
      try {
        this.loading = true

        const disableForm =
          (this.subscription.is_has_order_unship_last || this.subscription.disable_next_delivery) &&
          !this.subscriptionConfirm.reopen_flag &&
          !this.isReopenOnTheDay

        if (!disableForm) {
          const card_seq = await this.registerCard()

          await this.updatePayment(card_seq)
        }

        await this.updateSubscription(this.id, this.subscriptionConfirm)
        if (this.subscriptionConfirm.reopen_flag) {
          await this.pushDataLayer()
        }

        this.$alert({
          message: this.$t('common.messages.updateSubscriptionSuccess'),
          type: 'success',
        })

        this.$store.commit('subscription/SET_SUB_LIST_TYPE', this.Constants.EC_LINE009.TABS_TYPE.NORMAL)

        this.$store.dispatch('menu/getInvalidSubscription')
        this.$router.push({ name: 'EC_LINE014' })
      } catch (error) {
        if (error.response?.data?.meta?.app_status_code === APP_STATUS.ERROR_UPDATE_PAYMENT_UNABLE) {
          this.upablePaymentUpdateFlag = true
        } else {
          this.handleError(error)
        }
      } finally {
        this.loading = false
        if (this.isLoginAmazon) {
          this.onAmazonLogout(AMAZON.ACCESS_TOKEN_015)
        }
      }
    },

    async authorizeByAmazon() {
      try {
        this.loading = true
        const { data } = await this.updateAmazonConfirm(AMAZON.ACCESS_TOKEN_015)

        if (data?.webCheckoutDetails?.amazonPayRedirectUrl) {
          this.$session[AMAZON.CART_LINE015] = JSON.stringify(this.subscription)
          this.$session[AMAZON.CART_LINE028] = JSON.stringify(this.subscriptionConfirm)
          window.location.href = data?.webCheckoutDetails?.amazonPayRedirectUrl
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },

    processAmazonRedirect() {
      if (
        this.$route.query.amazonCheckoutSessionId &&
        !!this.$session[AMAZON.CART_LINE015] &&
        !!this.$session[AMAZON.CART_LINE028]
      ) {
        const subscription = cloneDeep(JSON.parse(this.$session[AMAZON.CART_LINE015]))
        const subscriptionConfirm = cloneDeep(JSON.parse(this.$session[AMAZON.CART_LINE028]))

        this.$store.commit('subscription/UPDATE_SUBSCRIPTION', subscription)
        this.$store.commit('subscription/SET_SUBSCRIPTION_CONFIRM', subscriptionConfirm)

        this.$session[AMAZON.CART_LINE015] = null
        this.$session[AMAZON.CART_LINE028] = null

        this.onUpdateSubscription()
      }
    },
    async registerCard() {
      if (this.isCreditCard && !this.subscriptionConfirm.payment.card_no) {
        const token = await this.getTokenCard({
          holdername: this.subscriptionConfirm.payment.card_holder,
          cardno: this.subscriptionConfirm.payment.card_number,
          expire: `20${this.subscriptionConfirm.payment.expiry_year.padStart(
            2,
            '0'
          )}${this.subscriptionConfirm.payment.expiry_month.padStart(2, '0')}`,
          securitycode: this.subscriptionConfirm.payment.security_code,
        })
        const card_info = {
          holder_name: this.subscriptionConfirm.payment.card_holder,
          expire: `${this.subscriptionConfirm.payment.expiry_month.padStart(
            2,
            '0'
          )}/${this.subscriptionConfirm.payment.expiry_year.padStart(2, '0')}`,
        }

        const { data } = await MyPageService.newCard({ token: token[0], gmo_id: this.user.gmo_id, card_info })
        return data.CardSeq
      } else {
        return null
      }
    },
    getCardInfo(val) {
      const cardInfo = this.subscriptionConfirm.cards.find((item) => item.card_seq == val)
      const [, expiry_year, expiry_month] = cardInfo.expire.split(/(\d{2})(\d{2})/)
      return {
        holder_name: cardInfo.holder_name,
        expire: `${expiry_month}/${expiry_year}`,
      }
    },
    async updatePayment(card_seq) {
      if (this.isAmazon && this.isCurrentAmazon && !this.isLoginAmazon) {
        return
      } else {
        const card_info =
          this.isCreditCard &&
          this.subscription.payment_service_id === this.Constants.PAYMENT_METHOD.CREDIT_CARD &&
          !isNil(this.subscription.credit_card_registration_seq)
            ? {
                before: this.getCardInfo(this.subscription.credit_card_registration_seq),
                after: this.getCardInfo(card_seq ? card_seq : this.subscriptionConfirm.payment.card_seq),
              }
            : undefined

        const body = {
          payment_id: this.subscriptionConfirm.payment.payment_id,
          ...(this.subscription.credit_card_registration_seq !== this.subscriptionConfirm.payment.card_seq && {
            card_info,
          }),
        }

        if (this.isCreditCard && card_seq) {
          body.credit_card_registration_seq = card_seq
        } else if (this.isCreditCard) {
          body.credit_card_registration_seq = this.subscriptionConfirm.payment.card_seq
        } else if (this.isAmazon) {
          body.amazon_checkout_session_id = this.$session[AMAZON.ACCESS_TOKEN_015]
        }

        await SubscriptionService.updateSubscriptionPayment(this.id, body)
      }
    },
    pushDataLayer() {
      window.dataLayer?.push({
        customer_id: this.user.id,
        subscription_id: this.id,
        subscription_status: this.subscription.status_name,
        resume_confirm: true,
      })
    },
  },
  metaInfo() {
    return {
      title: this.$t('ec-line028.title'),
      script: [
        {
          async: true,
          src: process.env.VUE_APP_AMAZON_PAY_WIDGET_URL,
        },
      ],
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.$t('ec-line028.description', { shop_name: this.shopName }),
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: this.$t('ec-line028.keyword', { shop_name: this.shopName }),
        },
      ],
    }
  },
}
</script>

<style lang="sass">
@import '@/assets/css/pages/ec-line028.scss'
</style>
